import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Segmented } from './Segmented';
import { ACCENT_COLORS, TEMPLATES } from '../theme/templates';
import { colors, formes, spacing } from '../theme/tokens';
import type { CvTemplate } from '../types';
import { t } from '../i18n';

interface TemplateSelection {
  template: CvTemplate;
  accentColor: string;
}

interface TemplatePickerProps {
  value: TemplateSelection;
  onChange: (value: TemplateSelection) => void;
  testID?: string;
}

/**
 * Choix du modèle sous l'aperçu : une bascule (Professionnel / Coloré) et, pour le coloré
 * seulement, la rangée de pastilles d'accent. L'aperçu au-dessus montre le résultat : plus de
 * vignettes à comparer. Ce sont les mêmes modèles et la même palette qu'avant, donc le PDF
 * exporté ne change pas.
 */
export function TemplatePicker({ value, onChange, testID }: TemplatePickerProps) {
  return (
    <View testID={testID} style={styles.container}>
      <Segmented
        accessibilityLabel={t('editors.template.groupLabel')}
        value={value.template}
        options={TEMPLATES.map((tpl) => ({
          key: tpl.key,
          label: t(`editors.template.${tpl.key}`),
          testID: `tpl-use-${tpl.key}`,
        }))}
        onChange={(key) => onChange({ template: key as CvTemplate, accentColor: value.accentColor })}
      />

      {/* Palette de couleurs : uniquement pour le modèle coloré. */}
      {value.template === 'accent' ? (
        <View
          testID="tpl-palette"
          accessibilityRole="radiogroup"
          accessibilityLabel={t('editors.template.accentColor')}
          style={styles.swatches}
        >
          {ACCENT_COLORS.map((c) => {
            const active = c.hex === value.accentColor;
            return (
              <Pressable
                key={c.hex}
                testID={`tpl-color-${c.hex}`}
                accessibilityRole="radio"
                accessibilityLabel={t(`editors.template.couleur.${c.label.toLowerCase()}`)}
                accessibilityState={{ checked: active }}
                onPress={() => onChange({ template: 'accent', accentColor: c.hex })}
                style={styles.cible}
              >
                <View style={[styles.swatch, { backgroundColor: c.hex }, active && styles.swatchActive]} />
              </Pressable>
            );
          })}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: spacing.sm },
  swatches: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center' },
  // Pastille de 28 pt dans une zone de toucher de 44 pt.
  cible: { width: formes.cible, height: formes.cible, alignItems: 'center', justifyContent: 'center' },
  swatch: { width: 28, height: 28, borderRadius: 14, borderWidth: 2, borderColor: colors.white },
  swatchActive: { borderColor: colors.night },
});
